import React, { useContext, useEffect, useState } from "react";
import "./SentimentHeatMap.css";
import { MdArrowDownward, MdArrowUpward, MdShuffle } from "react-icons/md";
import { ConceptsContext } from "../../../contexts";
import { useGetSentimentsMutation } from "../../../store/api/lumi/sentiment";
import { getConceptFilter } from "../../../utils/getConceptFilter";
import { debounce } from "lodash";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";

function SentimentHeatmap(props) {
    const [heatmapData, setHeatmapData] = useState([]);
    const [sortField, setSortField] = useState("");
    const [sortOrder, setSortOrder] = useState("asc");
    const [defaultData, setDefaultData] = useState([]);
    const [getSentiments] = useGetSentimentsMutation();
    const [isLoading, seIstLoading] = useState(false);

    async function getSentimentsData() {
        seIstLoading(true);
        const conceptFilter = getConceptFilter(props);

        const { data: dataConcepts } = await getSentiments({
            id: props.id,
            concept_selector: conceptFilter,
            filter: props.filters,
        });

        props.setFilterDocCount(dataConcepts.filter_count);
        setDefaultData(dataConcepts.match_counts);

        if (props && props.conceptList) {
            props.setLocalConceptOptions(dataConcepts.match_counts);
        }
        let results = dataConcepts.match_counts;
        const pageSize = Number(props.selectedPageSize);
        if (pageSize && results.length > pageSize) {
            results = results.slice(0, pageSize);
        }

        const sortedFetchedData = sortData(results).sort(
            (a, b) => b.exact_match_count - a.exact_match_count
        );
        setHeatmapData(sortedFetchedData);
        seIstLoading(false);
    }

    useEffect(() => {
        const sortedFetchedData = sortData(heatmapData);
        setHeatmapData(sortedFetchedData);
    }, [sortField, sortOrder]);

    const sortData = (data) => {
        if (sortOrder === "shuffle") {
            return defaultData;
        } else {
            return [...data].sort((a, b) => {
                if (sortField === "concept") {
                    return sortOrder === "asc"
                        ? a.name.localeCompare(b.name)
                        : b.name.localeCompare(a.name);
                } else {
                    const sentimentA = a.sentiment_share[sortField] || 0;
                    const sentimentB = b.sentiment_share[sortField] || 0;
                    return sortOrder === "asc"
                        ? sentimentA - sentimentB
                        : sentimentB - sentimentA;
                }
            });
        }
    };

    useEffect(() => {
        seIstLoading(true);
        const debouncedGetVolume = debounce(getSentimentsData, 500);

        debouncedGetVolume();

        return () => {
            debouncedGetVolume.cancel();
        };
    }, [
        props.conceptList,
        props.seletctedType,
        props.selectedPageSize,
        props.filters,
        props.conceptTypeDay,
        props.selectedLocalConcepts.length,
    ]);

    const concepts = [...new Set(heatmapData.map((item) => item.name))];
    const sentimentTypes = ["positive", "neutral", "negative"];

    const heatmapMatrix = concepts.map((concept) => {
        return sentimentTypes.map((sentimentType) => {
            const matchingData = heatmapData.find(
                (item) =>
                    item.name === concept && item.sentiment_share[sentimentType]
            );
            return matchingData
                ? matchingData.sentiment_share[sentimentType]
                : 0;
        });
    });

    const handleSort = (field) => {
        if (field === sortField && sortOrder !== "shuffle") {
            setSortOrder(sortOrder === "asc" ? "desc" : "shuffle");
        } else {
            setSortField(field);
            setSortOrder("asc");
        }
    };

    const renderSortIcon = (field) => {
        if (sortField === field) {
            if (sortOrder === "asc") {
                return <MdArrowUpward />;
            } else if (sortOrder === "desc") {
                return <MdArrowDownward />;
            } else if (sortOrder === "shuffle") {
                return <MdShuffle />;
            }
        }
        return null;
    };

    const { activeConcepts, setActiveConcepts } = useContext(ConceptsContext);

    if (isLoading) {
        return (
            <Spin
                className={"spinner-by-center"}
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            />
        );
    }
    return (
        <div style={{ width: "100%" }}>
            <div className="heatmap-widget-container">
                <div className="heatmap-container" style={{height: props.widget.layout.h * 39 - 150}}>
                    <div className="heatmap-header">
                        <table className="heatmap-table" >
                            <thead>
                            <tr>
                                <th
                                    onMouseDown={e => e.stopPropagation()}
                                    onClick={() => handleSort("concept")}
                                    style={{ fontSize: '18px', fontWeight: 'bold' }} // Increased font size and weight
                                >
                                    Concepts{renderSortIcon("concept")}{" "}
                                </th>
                                {sentimentTypes.map((sentimentType) => (
                                    <th onMouseDown={e => e.stopPropagation()}
                                        key={sentimentType}
                                        onClick={() =>
                                            handleSort(sentimentType)
                                        }
                                        style={{ fontSize: '17px', fontWeight: 'bold' }} // Increased font size and weight
                                    >
                                        {sentimentType}
                                        {renderSortIcon(sentimentType)}
                                    </th>
                                ))}
                            </tr>
                            </thead>
                            <tbody>
                            {concepts.map((concept, rowIndex) => (
                                <tr key={concept} className="heatmap-row">
                                    <td
                                        className="heatmap-theme-text"
                                        style={{
                                            cursor: "pointer",
                                            width: "40%",
                                            fontSize: '16px', // Increased font size
                                            fontWeight: 'bold',
                                        }}
                                        onClick={() => {
                                            if (
                                                activeConcepts.includes(
                                                    concept
                                                )
                                            ) {
                                                setActiveConcepts(
                                                    activeConcepts.filter(
                                                        (c) => c !== concept
                                                    )
                                                );
                                                return;
                                            }
                                            setActiveConcepts([
                                                ...activeConcepts,
                                                concept,
                                            ]);
                                        }}
                                    >
                                        {concept}
                                    </td>
                                    {sentimentTypes.map(
                                        (sentimentType, colIndex) => (
                                            <td
                                                className="heatmap-theme-text"
                                                key={sentimentType}
                                                style={{
                                                    backgroundColor: `rgba(${
                                                        sentimentType ===
                                                        "positive"
                                                            ? "0, 128, 0"
                                                            : sentimentType ===
                                                            "neutral"
                                                                ? "255, 255, 255"
                                                                : "255, 0, 0"
                                                    }, ${heatmapMatrix[rowIndex][colIndex]})`,
                                                    width: `${20}%`,
                                                    fontSize: '14px', // Increased font size
                                                    fontWeight: 'bold',
                                                }}
                                            >
                                                {(
                                                    heatmapMatrix[rowIndex][
                                                        colIndex
                                                        ] * 100
                                                ).toFixed(2)}
                                                %
                                            </td>
                                        )
                                    )}
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SentimentHeatmap;
